import { Ring, Sparkline } from '../../charts'
import { CfoExplainableValue } from '../../analysis'
import { basisDelta, trendValues } from '../ceoCockpitModel'
import { computeDeltaGrammar, formatCurrencyExact, formatDeltaGrammar } from '../ceoFormat'
import type { CeoBasisKind, CeoPeriodBlock, CeoScorecardMetric } from '../ceoCockpitTypes'

export type StatTileProps = {
  metric: CeoScorecardMetric
  block: CeoPeriodBlock
  basis: CeoBasisKind
  elapsedLabel?: string
  onExplain?: () => void
}

/** "Jul 31" from an ISO date, for the balance tile's close reference. */
function closeDayLabel(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function StatTile({ metric, block, basis, elapsedLabel, onExplain }: StatTileProps) {
  const prepared = block.actual != null && Number.isFinite(block.actual)
  const isPoint = metric.kind === 'point_in_time'
  // The trend ends on the open month, which is always smaller than a closed
  // one — drawn as-is every sparkline finished with a cliff. Show closed
  // months only; the tile's own number is the current-month reading.
  const trend = trendValues(metric.trend_12m)
  const sparkValues = metric.mtd?.complete === false && trend.length > 1 ? trend.slice(0, -1) : trend
  // A balance has no plan to be behind. Comparing it against the last close is
  // the question a CEO is actually asking of a cash or A/R number, so the tile
  // swaps basis rather than reporting the plan reference as missing.
  const delta = isPoint
    ? {
        deltaAbs: block.prior_close?.delta_abs ?? block.prior_period.delta_abs ?? null,
        deltaPct: block.prior_close?.delta_pct ?? block.prior_period.delta_pct ?? null,
      }
    : basisDelta(block, basis)
  const grammar = computeDeltaGrammar({ deltaAbs: delta.deltaAbs, deltaPct: delta.deltaPct, polarity: metric.polarity })
  const pace = block.pace
  const closeLabel = isPoint ? closeDayLabel(block.prior_close?.window?.end) : null
  const body = (
    <article className={`ceo-stat-tile ${grammar.tone}`} data-testid={`ceo-stat-${metric.id}`} data-fg-tour={`ceo-stat-${metric.id}`}>
      <span className="ceo-kicker">{metric.label}</span>
      <strong className="ceo-stat-value">
        {prepared ? formatCurrencyExact(block.actual) : 'Not prepared'}
      </strong>
      {prepared && isPoint && delta.deltaAbs != null && Number.isFinite(delta.deltaAbs) ? (
        <p className={`ceo-delta is-${grammar.tone}`}>
          {`${grammar.arrow} ${formatCurrencyExact(Math.abs(delta.deltaAbs))} vs ${closeLabel ? `${closeLabel} close` : 'last close'}`}
        </p>
      ) : prepared && !isPoint ? (
        <p className={`ceo-delta is-${grammar.tone}`}>
          {formatDeltaGrammar({ deltaAbs: delta.deltaAbs, deltaPct: delta.deltaPct, basis, polarity: metric.polarity })}
        </p>
      ) : prepared ? null : (
        <p className="ceo-delta">Not prepared vs {basis.replace('_', ' ')}</p>
      )}
      {isPoint ? (
        <p className="ceo-pace-meta">as of yesterday</p>
      ) : pace?.projection != null ? (
        <div className="ceo-pace-meta">
          <Ring value={Math.max(0, Math.min(1, (pace.pace_pct ?? 0) > 0 ? Math.min(pace.pace_pct as number, 1.4) / 1.4 : 0))} size={14} stroke={2} label="" ariaLabel={`${metric.label} pace`} />
          on pace for {formatCurrencyExact(pace.projection)}
        </div>
      ) : elapsedLabel ? (
        <p className="ceo-pace-meta">{elapsedLabel}</p>
      ) : null}
      {sparkValues.length >= 2 ? (
        <Sparkline values={sparkValues} width={60} height={22} down={grammar.tone === 'neg'} ariaLabel={`${metric.label} trend, closed months`} />
      ) : null}
    </article>
  )
  if (!onExplain) return body
  return (
    <CfoExplainableValue hint={`Ask AI to explain ${metric.label}`} onAnalyze={onExplain} ariaLabel={`Explain ${metric.label}`}>
      {body}
    </CfoExplainableValue>
  )
}
